import React, { useEffect, useMemo, useState } from "react";
import { formatDateTime } from "../utils/date";
import AppShell from "../components/AppShell";
import { useAuth } from "../contexts/AuthContext";
import useModules from "../hooks/useModules";
import { api, formatApiError } from "../lib/api";
import { ROLES } from "../lib/rbac";
import { Button } from "../components/ui/button";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Sparkles, Wifi, WifiOff, Copy, Plus, Power, PowerOff, Send, RefreshCw, Cable, Cloud, Activity,
    LogIn, LogOut, CheckCircle2, Circle, Trash2, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const CONNECTION_LABEL = {
    adms_push: "ADMS Push (cloud)",
    pull_sdk: "Pull SDK (LAN)",
    mock: "Mock / Generator",
    hikvision_isapi: "Hikvision ISAPI (push)",
};

// Iter60.p1 — canonical pairing config.
const DEVICE_ROLES = [
    { key: "check_in",  label: "Check-in",  Icon: LogIn },
    { key: "check_out", label: "Check-out", Icon: LogOut },
];

function RolePill({ role }) {
    const cfg = DEVICE_ROLES.find((r) => r.key === role);
    if (!cfg) {
        return (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 ring-1 ring-amber-200 inline-flex items-center gap-1">
                unset
            </span>
        );
    }
    const Icon = cfg.Icon;
    const cls = role === "check_in"
        ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
        : "bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200";
    return (
        <span className={`text-[10px] px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${cls}`}
              data-testid={`bio-role-pill-${role}`}>
            <Icon className="h-3 w-3" />
            {cfg.label}
        </span>
    );
}

/* Iter60.p2 — Fixed-mode chip. Shows "Mode: Check-In (fixed)" or
   "Mode: Check-Out (fixed)" with a coloured dot:
     - green: last sync ok
     - amber: never synced OR unreachable (server saved config, needs re-sync)
     - red:   sync failed with a non-network error (HTTP 4xx/5xx from device)
*/
function FixedModeChip({ device }) {
    if (device.connection_type !== "hikvision_isapi") return null;
    const role = device.role || "check_in";
    const label = role === "check_out" ? "Check-Out" : "Check-In";
    const result = device.last_mode_sync_result || null;
    const hasSynced = !!device.last_mode_sync_at;
    let dotClass = "bg-amber-500";
    let tooltip = "Never synced — click Sync Config once the terminal is reachable.";
    if (hasSynced && result) {
        if (result.ok) {
            dotClass = "bg-emerald-500";
            tooltip = `Last sync ok · ${new Date(device.last_mode_sync_at).toLocaleString()}`;
        } else if (result.error === "unreachable" || (result.error || "").startsWith("network_")) {
            dotClass = "bg-amber-500";
            tooltip = `Server saved config; terminal was unreachable. Retry when device is online.`;
        } else {
            dotClass = "bg-red-500";
            tooltip = `Sync failed: ${result.error || "unknown"}`;
        }
    }
    return (
        <span
            title={tooltip}
            className="text-[10px] px-2 py-0.5 rounded-full inline-flex items-center gap-1.5 bg-slate-100 text-slate-700 ring-1 ring-slate-200"
            data-testid={`bio-mode-chip-${device.serial_number}`}
        >
            <span className={`h-1.5 w-1.5 rounded-full ${dotClass}`} data-testid={`bio-mode-dot-${device.serial_number}`} />
            Mode: {label} (fixed)
        </span>
    );
}

function StatusPill({ status }) {
    const map = {
        online:   { cls: "bg-emerald-50 text-emerald-700",           icon: Wifi },
        offline:  { cls: "bg-gray-100 text-gray-500",                 icon: WifiOff },
        disabled: { cls: "bg-red-100 text-red-800 ring-1 ring-red-300 font-bold uppercase tracking-wider", icon: PowerOff },
    };
    const cfg = map[status] || map.offline;
    const Icon = cfg.icon;
    return (
        <span className={`text-[10px] px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${cfg.cls}`}>
            <Icon className="h-3 w-3" />
            {status}
        </span>
    );
}

function CopyChip({ label, value, testid }) {
    if (!value) return null;
    const copy = async () => {
        try { await navigator.clipboard.writeText(value); toast.success(`${label} copied`); }
        catch { toast.error("Copy failed"); }
    };
    return (
        <button
            onClick={copy}
            className="text-[10px] mono inline-flex items-center gap-1 px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-700"
            data-testid={testid}
        >
            <Copy className="h-3 w-3" />
            {value.length > 24 ? `${value.slice(0, 12)}…${value.slice(-8)}` : value}
        </button>
    );
}

/* ------------------------------------------------------------------ */
/* Iter60.p1 — Pairing Summary                                        */
/* ------------------------------------------------------------------ */
/* At-a-glance card grid: for each module, show its 2 role slots
   (check-in + check-out) as filled or empty. Filled slot shows the
   device name + status pill; empty slot shows a placeholder + a
   quick-add button that pre-fills the module + role in the Add
   Device dialog.                                                    */
function PairingSummary({ devices, modules, onQuickAdd, isAdmin }) {
    if (!modules || modules.length === 0) return null;
    return (
        <section className="mb-2" data-testid="bio-pairing-summary">
            <div className="text-label mb-2" style={{ color: "var(--asl-text-2)" }}>
                Paired Devices — per Module
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {modules.map((m) => {
                    // Iter60.p6 — disabled devices don't count toward pairing.
                    // The backend validator excludes them; the UI tally must
                    // agree so admins see the correct slot state.
                    const modDevices = devices.filter(
                        (d) => d.module_id === m.id && !d.deleted_at && d.status !== "disabled"
                    );
                    const byRole = {
                        check_in:  modDevices.find((d) => d.role === "check_in"),
                        check_out: modDevices.find((d) => d.role === "check_out"),
                    };
                    const filled = Object.values(byRole).filter(Boolean).length;
                    return (
                        <div key={m.id}
                            className="asl-card p-4"
                            data-testid={`bio-pairing-module-${m.code || m.id}`}>
                            <div className="flex items-center justify-between mb-3">
                                <div>
                                    <div className="text-sm font-semibold asl-navy-text">{m.name}</div>
                                    <div className="text-[11px]" style={{ color: "var(--asl-text-3)" }}>
                                        {m.code || m.id.slice(0, 8)}
                                    </div>
                                </div>
                                <div className="text-[11px] font-semibold"
                                    style={{ color: filled === 2 ? "var(--status-success)" : "var(--asl-text-3)" }}
                                    data-testid={`bio-slot-count-${m.code || m.id}`}>
                                    {filled}/2 devices
                                </div>
                            </div>
                            <div className="space-y-2">
                                {DEVICE_ROLES.map((r) => {
                                    const d = byRole[r.key];
                                    const RoleIcon = r.Icon;
                                    return (
                                        <div key={r.key}
                                            className="flex items-center gap-2 p-2 rounded-md"
                                            style={{
                                                background: d ? "var(--surface-muted)" : "transparent",
                                                border: d ? "none" : "1px dashed var(--border-default)",
                                            }}
                                            data-testid={`bio-slot-${m.code || m.id}-${r.key}`}>
                                            <RoleIcon className="h-4 w-4" style={{ color: d ? "var(--asl-navy)" : "var(--asl-text-3)" }} />
                                            <div className="flex-1 min-w-0">
                                                <div className="text-xs font-semibold" style={{ color: "var(--asl-text-2)" }}>
                                                    {r.label}
                                                </div>
                                                {d ? (
                                                    <div className="text-[11px] truncate" style={{ color: "var(--asl-text)" }}>
                                                        {d.device_name}
                                                        <span className="mono ml-1" style={{ color: "var(--asl-text-3)" }}>
                                                            · {d.serial_number}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <div className="text-[11px]" style={{ color: "var(--asl-text-3)" }}>
                                                        Empty slot
                                                    </div>
                                                )}
                                            </div>
                                            {d ? <StatusPill status={d.status || "offline"} /> : (
                                                isAdmin && (
                                                    <button
                                                        type="button"
                                                        onClick={() => onQuickAdd?.({ module_id: m.id, role: r.key })}
                                                        className="text-[11px] px-2 py-1 rounded-full"
                                                        style={{
                                                            background: "rgba(168, 50, 74, 0.10)",
                                                            color: "var(--asl-crimson)",
                                                        }}
                                                        data-testid={`bio-quickadd-${m.code || m.id}-${r.key}`}
                                                    >
                                                        + Add
                                                    </button>
                                                )
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}

function DeviceCard({ d, modulesById, isAdmin, isHR, onRefresh, onEdit, onCommand, onQueue, baseUrl }) {
    const [busy, setBusy] = useState(false);
    const isAdms = d.connection_type === "adms_push";
    const isPull = d.connection_type === "pull_sdk";
    const isMock = d.connection_type === "mock";
    const isHik  = d.connection_type === "hikvision_isapi";

    const generateSample = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/attendance/biometric-mock/generate-sample", {
                module_id: d.module_id,
                work_date: new Date().toISOString().slice(0, 10),
            });
            toast.success(`Generated ${data.created} events (${data.absent} absent)`);
            onRefresh();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    // Iter60.p2 — manual "Sync Config" button: re-pushes the fixed
    // attendance-mode to the terminal (auto mode + role-locked
    // schedule). Uses a loading toast that resolves to the final
    // result so the click always produces user feedback.
    const syncConfig = async () => {
        const roleLabel = d.role === "check_out" ? "Check-Out" : "Check-In";
        const toastId = toast.loading(`Syncing ${roleLabel} mode to ${d.device_name || "device"}…`);
        setBusy(true);
        try {
            const { data } = await api.post(`/biometric-devices/${d.id}/sync-config`);
            if (data?.ok) {
                toast.success(`Mode locked to ${roleLabel} (fixed)`, { id: toastId });
            } else if (data?.error === "unreachable") {
                toast.warning("Config saved; terminal unreachable — retry once it's online.", { id: toastId });
            } else {
                toast.error(`Sync failed: ${data?.error || "unknown"}`, { id: toastId });
            }
            onRefresh();
        } catch (e) {
            toast.error(`Sync failed: ${formatApiError(e) || "request failed"}`, { id: toastId });
        } finally {
            setBusy(false);
        }
    };

    const testConn = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/devices/${d.id}/test-connection`);
            if (data.ok) toast.success(`Connected. Firmware: ${data.firmware_version || "—"}`);
            else toast.error(`Connection failed: ${data.error || "unknown"}`);
            onRefresh();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const syncNow = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/devices/${d.id}/sync-now`);
            if (data.ignored) toast.info(data.message);
            else if (data.ok) toast.success(`Synced ${data.accepted} events (${data.mapped} mapped, ${data.unmapped} unmapped)`);
            else toast.error(`Sync failed: ${data.error || "unknown"}`);
            onRefresh();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const toggleEnabled = async () => {
        setBusy(true);
        try {
            const url = d.status === "disabled" ? `/devices/${d.id}/enable` : `/devices/${d.id}/disable`;
            await api.post(url);
            toast.success(d.status === "disabled" ? "Device enabled" : "Device disabled");
            onRefresh();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    // Iter60.p6 — Delete flow. Soft-deletes via
    // DELETE /api/biometric-devices/{id} (already exists per p1).
    // Attendance events tied to this device stay intact; only the
    // pairing slot is freed.
    const [confirmDelete, setConfirmDelete] = useState(false);
    const deleteDevice = async () => {
        setBusy(true);
        try {
            await api.delete(`/biometric-devices/${d.id}`);
            toast.success(`Device "${d.device_name || d.serial_number}" deleted. Pairing slot freed.`);
            setConfirmDelete(false);
            onRefresh();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const lastSeen = d.last_seen ? formatDateTime(d.last_seen) : "Never";
    const lastSync = d.last_sync_at ? formatDateTime(d.last_sync_at) : "Never";

    return (
        <div className="asl-card p-5" data-testid={`bio-device-${d.serial_number}`}>
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="text-[11px] mono asl-crimson-text">{d.serial_number}</div>
                    <div className="text-base font-bold asl-navy-text mt-0.5">{d.device_name}</div>
                    <div className="text-[11px] text-[color:var(--asl-muted)] mt-0.5">{d.device_model || "—"}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                    <StatusPill status={d.status} />
                    <RolePill role={d.role} />
                    <FixedModeChip device={d} />
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 inline-flex items-center gap-1">
                        {isAdms ? <Cloud className="h-3 w-3" /> : isPull ? <Cable className="h-3 w-3" /> : <Sparkles className="h-3 w-3" />}
                        {CONNECTION_LABEL[d.connection_type] || d.connection_type}
                    </span>
                </div>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-x-2 gap-y-1 text-[11px] text-[color:var(--asl-muted)]">
                <div>Module: <span className="asl-navy-text font-medium">{modulesById[d.module_id]?.name || "—"}</span></div>
                <div>Worksite: <span className="asl-navy-text">{d.worksite || "—"}</span></div>
                <div>Events today: <span className="asl-navy-text font-semibold">{d.events_today ?? 0}</span></div>
                <div className="inline-flex items-center gap-1"><Activity className="h-3 w-3" />FW: {d.firmware_version || "—"}</div>
                <div className="col-span-2 mono">Last seen: {lastSeen}</div>
                <div className="col-span-2 mono">Last sync: {lastSync}</div>
                {isPull && (
                    <div className="col-span-2 mono">{d.ip_address || "no-ip"}:{d.tcp_port || 4370}</div>
                )}
            </div>

            {isAdms && (
                <div className="mt-3 p-3 rounded-md bg-[color:var(--asl-bg,#F5F6F8)] space-y-2">
                    <div className="text-[11px] font-semibold asl-navy-text">Device setup</div>
                    <div className="text-[10px] text-[color:var(--asl-muted)]">Configure the ZKTeco device&apos;s ADMS server. Pick the URL that matches your environment:</div>
                    <div className="space-y-1.5">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[10px] font-semibold asl-crimson-text px-1.5 py-0.5 rounded bg-red-50">Preview (testing only)</span>
                            <CopyChip label="Preview URL" value={`${baseUrl}/api/iclock/`} testid={`bio-copy-url-preview-${d.serial_number}`} />
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[10px] font-semibold text-emerald-700 px-1.5 py-0.5 rounded bg-emerald-50">Production (after reverse-proxy config)</span>
                            <CopyChip label="Production URL" value="https://aslwms.com/iclock/" testid={`bio-copy-url-prod-${d.serial_number}`} />
                        </div>
                        <div className="flex flex-wrap gap-1.5 pt-1">
                            <CopyChip label="Serial number" value={d.serial_number} testid={`bio-copy-sn-${d.serial_number}`} />
                            <CopyChip label="Auth token" value={d.push_token} testid={`bio-copy-token-${d.serial_number}`} />
                        </div>
                    </div>
                    <div className="text-[10px] text-[color:var(--asl-muted)] pt-1 border-t" style={{ borderColor: "var(--asl-border)" }}>
                        Production requires the reverse-proxy at <span className="mono">aslwms.com</span> to forward <span className="mono">/iclock/*</span> to this backend. Until that&apos;s wired, use the Preview URL to test.
                    </div>
                </div>
            )}

            {isHR && (
                <div className="flex flex-wrap gap-2 mt-4">
                    {isMock && (
                        <Button size="sm" className="asl-navy-bg text-white" onClick={generateSample} disabled={busy}
                                data-testid={`bio-generate-${d.serial_number}`}>
                            <Sparkles className="h-3 w-3 mr-1" /> Generate Sample Day
                        </Button>
                    )}
                    {isPull && (
                        <>
                            <Button size="sm" variant="outline" onClick={testConn} disabled={busy}
                                    data-testid={`bio-test-${d.serial_number}`}>
                                <Cable className="h-3 w-3 mr-1" /> Test Connection
                            </Button>
                            <Button size="sm" className="asl-navy-bg text-white" onClick={syncNow} disabled={busy}
                                    data-testid={`bio-syncnow-${d.serial_number}`}>
                                <RefreshCw className="h-3 w-3 mr-1" /> Sync Now
                            </Button>
                        </>
                    )}
                    {isHik && isAdmin && (
                        <Button size="sm" variant="outline" onClick={syncConfig} disabled={busy}
                                data-testid={`bio-syncconfig-${d.serial_number}`}
                                title="Push fixed attendance mode (no on-screen picker) to this terminal">
                            <RefreshCw className="h-3 w-3 mr-1" /> Sync Config
                        </Button>
                    )}
                    {isAdms && (
                        <Button size="sm" variant="outline" onClick={() => toast.info("Push devices auto-sync; data appears as the device pushes it.")}
                                data-testid={`bio-syncnow-${d.serial_number}`}>
                            <RefreshCw className="h-3 w-3 mr-1" /> Sync Now
                        </Button>
                    )}
                    {!isMock && (
                        <Button size="sm" variant="outline" onClick={() => onCommand(d)}
                                data-testid={`bio-cmd-${d.serial_number}`}>
                            <Send className="h-3 w-3 mr-1" /> Push to Device
                        </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => onQueue(d)}
                            data-testid={`bio-queue-${d.serial_number}`}>
                        View Queue
                    </Button>
                    {isAdmin && (
                        <>
                            <Button size="sm" variant="outline" onClick={() => onEdit(d)}
                                    data-testid={`bio-edit-${d.serial_number}`}>
                                Edit
                            </Button>
                            <Button size="sm" variant="outline" onClick={toggleEnabled} disabled={busy}
                                    data-testid={`bio-toggle-${d.serial_number}`}>
                                {d.status === "disabled" ? <><Power className="h-3 w-3 mr-1" /> Enable</> : <><PowerOff className="h-3 w-3 mr-1" /> Disable</>}
                            </Button>
                            <Button size="sm" variant="outline"
                                    className="text-red-700 border-red-200 hover:bg-red-50"
                                    onClick={() => setConfirmDelete(true)} disabled={busy}
                                    data-testid={`bio-delete-${d.serial_number}`}
                                    title="Soft-delete this device and free the pairing slot">
                                <Trash2 className="h-3 w-3 mr-1" /> Delete
                            </Button>
                        </>
                    )}
                </div>
            )}

            {/* Iter60.p6 — Delete confirmation. Soft-delete only; events remain. */}
            <Dialog open={confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(false)}>
                <DialogContent className="max-w-md" data-testid={`bio-delete-confirm-${d.serial_number}`}>
                    <DialogHeader>
                        <DialogTitle className="text-red-700 flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5" />
                            Delete device &ldquo;{d.device_name || d.serial_number}&rdquo;?
                        </DialogTitle>
                        <DialogDescription className="text-xs">
                            This will free the pairing slot on <b>{modulesById[d.module_id]?.name || "this module"}</b> so
                            you can register a replacement. Attendance events already tied to this device stay intact —
                            only the device row is soft-deleted and can be restored later by an admin.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="rounded-md p-3 text-xs bg-slate-50 border border-slate-200">
                        <div><span className="font-semibold">Serial:</span> <span className="mono">{d.serial_number || "—"}</span></div>
                        <div><span className="font-semibold">Role:</span> {d.role || "unset"}</div>
                        <div><span className="font-semibold">Status:</span> {d.status || "offline"}</div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirmDelete(false)}
                                data-testid={`bio-delete-cancel-${d.serial_number}`}>Cancel</Button>
                        <Button className="bg-red-600 hover:bg-red-700 text-white"
                                onClick={deleteDevice} disabled={busy}
                                data-testid={`bio-delete-confirm-btn-${d.serial_number}`}>
                            {busy ? <><RefreshCw className="h-3 w-3 mr-1 animate-spin" /> Deleting…</> : <>Yes, delete</>}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default function BiometricDevices() {
    const { user } = useAuth();
    const isAdmin = user?.role === ROLES.KAAFI_ADMIN;
    const isHR = [ROLES.KAAFI_ADMIN, ROLES.KAAFI_HR].includes(user?.role);
    const { byId: modulesById, modules } = useModules();
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addOpen, setAddOpen] = useState(false);
    // Iter60.p1 — quick-add pre-fill (module + role from PairingSummary).
    const [addPrefill, setAddPrefill] = useState(null);
    const [editOpen, setEditOpen] = useState(null);
    const [cmdOpen, setCmdOpen] = useState(null);
    const [queueOpen, setQueueOpen] = useState(null);
    const baseUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/devices");
            setDevices(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const groups = useMemo(() => ({
        adms: devices.filter((d) => d.connection_type === "adms_push"),
        pull: devices.filter((d) => d.connection_type === "pull_sdk"),
        mock: devices.filter((d) => d.connection_type === "mock"),
        hikvision: devices.filter((d) => d.connection_type === "hikvision_isapi"),
    }), [devices]);

    return (
        <AppShell title="Biometric Devices">
            <section className="space-y-6" data-testid="biometric-page">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <h2 className="text-xl font-bold asl-navy-text">Biometric Devices</h2>
                        <p className="text-sm text-[color:var(--asl-muted)]">
                            ZKTeco PRO 200 devices push attendance to <span className="mono">/iclock/</span>.
                            Pull-SDK and mock devices remain supported as fallbacks.
                        </p>
                    </div>
                    {isAdmin && (
                        <Button onClick={() => { setAddPrefill(null); setAddOpen(true); }} className="asl-navy-bg text-white"
                                data-testid="bio-add-device">
                            <Plus className="h-4 w-4 mr-1" /> Add Device
                        </Button>
                    )}
                </div>

                {/* Iter60.p1 — pairing summary card grid (3 modules × 2 slots) */}
                {!loading && (
                    <PairingSummary
                        devices={devices}
                        modules={modules}
                        isAdmin={isAdmin}
                        onQuickAdd={(pf) => { setAddPrefill(pf); setAddOpen(true); }}
                    />
                )}

                {loading ? (
                    <div className="text-sm text-[color:var(--asl-muted)]">Loading devices…</div>
                ) : (
                    <>
                        <Section title="Real Devices — ADMS Push" hint="Devices push events to the server. Used in production." testid="bio-section-adms">
                            {groups.adms.length === 0 ? (
                                <Empty msg="No ADMS-push devices yet." />
                            ) : (
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    {groups.adms.map((d) => (
                                        <DeviceCard key={d.id} d={d} modulesById={modulesById} isAdmin={isAdmin} isHR={isHR}
                                                    onRefresh={load} onEdit={setEditOpen} onCommand={setCmdOpen}
                                                    onQueue={setQueueOpen} baseUrl={baseUrl} />
                                    ))}
                                </div>
                            )}
                        </Section>

                        <Section title="LAN Devices — Pull SDK" hint="Server connects to the device over TCP/IP (port 4370)." testid="bio-section-pull">
                            {groups.pull.length === 0 ? (
                                <Empty msg="No pull-SDK devices configured." />
                            ) : (
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    {groups.pull.map((d) => (
                                        <DeviceCard key={d.id} d={d} modulesById={modulesById} isAdmin={isAdmin} isHR={isHR}
                                                    onRefresh={load} onEdit={setEditOpen} onCommand={setCmdOpen}
                                                    onQueue={setQueueOpen} baseUrl={baseUrl} />
                                    ))}
                                </div>
                            )}
                        </Section>

                        <Section title="Hikvision ISAPI Devices — Face Terminals" hint="DS-K1T342 series terminals push AccessControllerEvent JSON to /api/hikvision/push." testid="bio-section-hikvision">
                            {groups.hikvision.length === 0 ? (
                                <Empty msg="No Hikvision ISAPI devices yet." />
                            ) : (
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    {groups.hikvision.map((d) => (
                                        <HikvisionCard
                                            key={d.id}
                                            d={d}
                                            modulesById={modulesById}
                                            isAdmin={isAdmin}
                                            baseUrl={baseUrl}
                                            onRefresh={load}
                                        />
                                    ))}
                                </div>
                            )}
                        </Section>

                        <Section title="Mock Devices — Demo / Generator" hint="For training and demos. Generate a synthetic day of attendance." testid="bio-section-mock">
                            {groups.mock.length === 0 ? (
                                <Empty msg="No mock devices." />
                            ) : (
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                    {groups.mock.map((d) => (
                                        <DeviceCard key={d.id} d={d} modulesById={modulesById} isAdmin={isAdmin} isHR={isHR}
                                                    onRefresh={load} onEdit={setEditOpen} onCommand={setCmdOpen}
                                                    onQueue={setQueueOpen} baseUrl={baseUrl} />
                                    ))}
                                </div>
                            )}
                        </Section>
                    </>
                )}
            </section>

            <AddDeviceDialog
                open={addOpen} onClose={() => { setAddOpen(false); setAddPrefill(null); }} modules={modules}
                devices={devices}
                prefill={addPrefill}
                onSaved={() => { setAddOpen(false); setAddPrefill(null); load(); }}
            />
            <EditDeviceDialog
                device={editOpen} modules={modules} onClose={() => setEditOpen(null)}
                onSaved={() => { setEditOpen(null); load(); }}
            />
            <CommandDialog device={cmdOpen} onClose={() => setCmdOpen(null)} />
            <QueueDialog device={queueOpen} onClose={() => setQueueOpen(null)} />
        </AppShell>
    );
}

function Section({ title, hint, children, testid }) {
    return (
        <div className="space-y-2" data-testid={testid}>
            <div>
                <h3 className="text-sm font-bold asl-navy-text">{title}</h3>
                <p className="text-[11px] text-[color:var(--asl-muted)]">{hint}</p>
            </div>
            {children}
        </div>
    );
}

function Empty({ msg }) {
    return <div className="asl-card p-5 text-sm text-[color:var(--asl-muted)] italic">{msg}</div>;
}

/**
 * Returns true when the IP is empty or falls into any RFC1918 private range:
 *   10.0.0.0/8, 172.16.0.0/12, 192.168.0.0/16.
 * Used to warn admins that the cloud backend cannot reach the device directly.
 */
function _isPrivateOrEmptyIp(ip) {
    if (!ip) return true;
    const trimmed = String(ip).trim();
    if (!trimmed) return true;
    const parts = trimmed.split(".");
    if (parts.length !== 4) return false;
    const o = parts.map((p) => parseInt(p, 10));
    if (o.some((n) => Number.isNaN(n) || n < 0 || n > 255)) return false;
    if (o[0] === 10) return true;
    if (o[0] === 172 && o[1] >= 16 && o[1] <= 31) return true;
    if (o[0] === 192 && o[1] === 168) return true;
    return false;
}

function HikvisionCard({ d, modulesById, isAdmin, baseUrl, onRefresh }) {
    const [busy, setBusy] = useState(false);
    const [credsOpen, setCredsOpen] = useState(false);
    const [testResult, setTestResult] = useState(null);
    // Iter60.p3 — Probe Mode modal state.
    const [probeOpen, setProbeOpen] = useState(false);
    const [probeData, setProbeData] = useState(null);
    // Iter60.p6 — Delete confirmation state.
    const [confirmHikDelete, setConfirmHikDelete] = useState(false);
    const module = modulesById[d.module_id];
    const previewPush = `${baseUrl}/api/hikvision/push`;
    const prodPush = "https://aslwms.com/api/hikvision/push";
    const isDisabled = d.status === "disabled";
    const ipLooksPrivate = _isPrivateOrEmptyIp(d.ip_address);
    // Iter60.p3 — surface the manual-fallback banner after ANY failed
    // Sync Config attempt (unreachable, no_variant_worked, permission
    // errors, etc.). The only case we suppress is "not_hikvision" which
    // means the helper was a soft no-op. The banner persists on the
    // card until the next Sync Config succeeds.
    const modeSync = d.last_mode_sync_result || null;
    const modeSyncAttempted = !!d.last_mode_sync_at;
    const modeSyncFailed = modeSyncAttempted && modeSync && modeSync.ok === false
        && modeSync.skipped !== "not_hikvision";
    const noVariantWorked = modeSyncFailed;
    // Cache the device IP + instructions text for the copy button.
    // Iter60.p4 — verified working path on DS-K1T342MFWX-E1 firmware
    // V4.39.180 build 250912. The device uses HTTPS with a self-signed
    // certificate, so instruct the user to accept the browser warning.
    const deviceIp = d.ip_address || "<device-ip>";
    const manualInstructionsText = [
        `Automatic mode-lock failed for this device firmware.`,
        ``,
        `The cloud backend cannot reach devices on your private office LAN.`,
        `Please disable the on-device picker manually from the device's web admin:`,
        ``,
        `1. On a computer connected to the same office network as the device, open a browser and go to:`,
        `   https://${deviceIp}   (accept the "not secure" warning — the device uses a self-signed certificate)`,
        ``,
        `2. Log in with your device admin credentials.`,
        ``,
        `3. In the top menu, click "System and Maintenance".`,
        ``,
        `4. In the left sidebar, click "System Configuration" → then click "Platform Attendance" (at the bottom of the sidebar).`,
        ``,
        `5. Turn OFF the "Attendance Status Required" toggle (it will change from green to grey).`,
        ``,
        `6. Click "Save" at the bottom of the page.`,
        ``,
        `7. Test on the physical device: present your face WITHOUT touching the screen.`,
        `   The device should now just say "Done" (or similar) — no more Check-In / Check-Out picker.`,
        ``,
        `Note: our backend already knows each device's role (check-in or check-out) and stamps`,
        `scans automatically. Turning off "Attendance Status Required" removes the on-device`,
        `picker without affecting attendance logging.`,
        ``,
        `Verified working on DS-K1T342MFWX-E1 firmware V4.39.180.`,
    ].join("\n");

    // Friendly error surface — catches ConnectTimeout / 599 / disabled_device.
    const _surfaceError = (err) => {
        const raw = formatApiError(err) || "";
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        const reason = (detail && typeof detail === "object") ? detail.reason : null;
        if (status === 409 && reason === "device_disabled") {
            toast.error("Device is disabled — enable it first", {
                description: detail?.message || "Toggle the power icon on the card, then try again.",
            });
            return;
        }
        if (/connect ?timeout|connecttimeout|network error|econnrefused|read ?timeout|599/i.test(raw)) {
            toast.error("Cannot reach device", {
                description:
                    "Your device is on a private LAN — the cloud can't reach it. Push mode still works; this button is optional. See the amber banner on the card.",
            });
            return;
        }
        toast.error(raw || "Request failed");
    };

    const call = async (path, verb = "post", body = null) => {
        setBusy(true);
        try {
            const { data } = await api[verb](path, body || {});
            return data;
        } catch (e) {
            _surfaceError(e);
            return null;
        } finally { setBusy(false); }
    };

    const doTest = async () => {
        const r = await call(`/devices/${d.id}/hikvision/test-connection`);
        if (r) {
            setTestResult(r);
            if (r.ok) toast.success("Test connection OK — device reachable.");
            else toast.error(r.error || "Test connection failed");
            onRefresh?.();
        }
    };
    const doApply = async () => {
        const r = await call(`/devices/${d.id}/hikvision/apply-config`);
        if (r?.ok) { toast.success("Listening host + time synced to device."); onRefresh?.(); }
        else if (r) toast.error(r.error || "Apply config failed");
    };
    const doSync = async () => {
        const r = await call(`/devices/${d.id}/hikvision/sync-users`);
        if (r) {
            const msg = r.ok
                ? `Sync users OK — ${r.pushed} pushed`
                : `Sync users had errors — ${r.pushed} pushed, ${r.failed} failed`;
            (r.ok ? toast.success : toast.error)(msg);
            onRefresh?.();
        }
    };
    // Iter60.p2 — Sync Config: push the fixed attendance mode
    // (auto + role-locked schedule) so the terminal stops asking
    // employees to pick check-in / check-out. Uses a loading toast
    // that resolves to a success / warning / error so users always
    // get feedback on the click.
    const doSyncConfig = async () => {
        const roleLabel = d.role === "check_out" ? "Check-Out" : "Check-In";
        const toastId = toast.loading(`Syncing ${roleLabel} mode to ${d.device_name || "device"}…`);
        setBusy(true);
        try {
            const { data } = await api.post(`/biometric-devices/${d.id}/sync-config`);
            if (data?.ok) {
                const via = data.working_variant ? ` (via ${data.working_variant})` : "";
                toast.success(`Mode locked to ${roleLabel} (fixed)${via}`, {
                    id: toastId,
                    description: data.applied_role
                        ? `Terminal now attributes every scan to ${data.hik_status}.`
                        : undefined,
                });
            } else if (data?.error === "unreachable") {
                toast.warning("Config saved; terminal unreachable — retry once it's online.", {
                    id: toastId,
                    description: `${d.device_name || "Device"} is on a private LAN. Push mode still works; this button is optional.`,
                });
            } else if (data?.error === "no_variant_worked") {
                toast.error("No ISAPI variant worked on this firmware.", {
                    id: toastId,
                    description: "Use the manual fallback below to lock the mode from the device's web admin.",
                });
            } else {
                toast.error(`Sync Config failed: ${data?.error || "unknown"}`, {
                    id: toastId,
                });
            }
            onRefresh?.();
        } catch (e) {
            const msg = formatApiError(e) || "Request failed";
            toast.error(`Sync Config failed: ${msg}`, { id: toastId });
        } finally {
            setBusy(false);
        }
    };

    // Iter60.p3 — Probe Mode: GET the device's current attendance-mode
    // config over ISAPI so an admin can see what the terminal currently
    // reports (and whether our push actually landed).
    // NOTE: we deliberately do NOT call onRefresh() here — that would
    // re-fetch the device list and remount this card, wiping the
    // modal's local `probeOpen` state before it can render.
    const doProbeMode = async () => {
        const toastId = toast.loading(`Probing ${d.device_name || "device"} for its current attendance mode…`);
        setBusy(true);
        try {
            const { data } = await api.get(`/biometric-devices/${d.id}/probe-mode`);
            setProbeData(data);
            setProbeOpen(true);
            if (data?.ok) {
                toast.success(`Probe OK — terminal reachable`, { id: toastId });
            } else if (data?.error === "unreachable") {
                toast.warning("Terminal unreachable. Retry once it's online.", { id: toastId });
            } else {
                toast.error(`Probe failed: ${data?.error || "unknown"}`, { id: toastId });
            }
        } catch (e) {
            toast.error(`Probe failed: ${formatApiError(e) || "request failed"}`, { id: toastId });
        } finally {
            setBusy(false);
        }
    };

    const enable = async (on) => {
        await call(`/devices/${d.id}/${on ? "enable" : "disable"}`);
        if (on) toast.success("Device enabled. Ready to receive events.");
        onRefresh?.();
    };

    // Iter60.p6 — Delete flow. Soft-deletes via
    // DELETE /api/biometric-devices/{id} (already exists per p1).
    // Attendance events tied to this device remain intact; only the
    // pairing slot is freed on the module.
    const deleteHikDevice = async () => {
        setBusy(true);
        try {
            await api.delete(`/biometric-devices/${d.id}`);
            toast.success(`Device "${d.device_name || d.serial_number}" deleted. Pairing slot freed.`);
            setConfirmHikDelete(false);
            onRefresh?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    // Guard: disabling a device that's had activity in the last 10 min
    // silently drops all inbound scans. Warn the operator before it happens.
    const [confirmDisable, setConfirmDisable] = useState(false);
    const activelyReceiving = useMemo(() => {
        if (!d.last_seen) return false;
        const t = Date.parse(d.last_seen);
        if (isNaN(t)) return false;
        return (Date.now() - t) < 10 * 60 * 1000;
    }, [d.last_seen]);

    const handleTogglePower = () => {
        // Enabling — no guard, just do it (toast handled in enable()).
        if (d.status === "disabled") {
            enable(true);
            return;
        }
        // Disabling — guard when the device is actively receiving events.
        if (activelyReceiving) {
            setConfirmDisable(true);
            return;
        }
        enable(false);
    };

    const lastSeenLabel = d.last_seen ? formatDateTime(d.last_seen) : "Never";

    return (
        <div
            className="asl-card p-5 space-y-4"
            style={{ borderLeft: "4px solid #A8324A" }}
            data-testid={`hikvision-card-${d.id}`}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-base font-bold asl-navy-text truncate">{d.device_name}</h3>
                        <StatusPill status={d.status} />
                        <RolePill role={d.role} />
                        <FixedModeChip device={d} />
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 inline-flex items-center gap-1">
                            <Cloud className="h-3 w-3" /> Hikvision ISAPI
                        </span>
                    </div>
                    <div className="text-[11px] text-[color:var(--asl-muted)] mt-1">
                        {d.device_model} · fw {d.firmware_version || "unknown"} · {module?.name || "—"}
                    </div>
                </div>
                {isAdmin && (
                    <div className="flex items-center gap-1">
                        <Button variant="outline" size="sm" onClick={handleTogglePower}
                            disabled={busy}
                            data-testid={`hik-toggle-${d.id}`}>
                            {busy ? <RefreshCw className="h-4 w-4 animate-spin" /> :
                                d.status === "disabled" ? <Power className="h-4 w-4" /> : <PowerOff className="h-4 w-4" />}
                        </Button>
                    </div>
                )}
            </div>

            {/* Confirmation dialog — only fires when disabling an actively-receiving device */}
            <Dialog open={confirmDisable} onOpenChange={(o) => !o && setConfirmDisable(false)}>
                <DialogContent className="max-w-md" data-testid={`hik-disable-confirm-${d.id}`}>
                    <DialogHeader>
                        <DialogTitle className="text-red-700 flex items-center gap-2">
                            <PowerOff className="h-5 w-5" /> Disable {d.device_name}?
                        </DialogTitle>
                        <DialogDescription className="text-xs">
                            This device is <b>actively receiving events</b> (last activity{" "}
                            {d.last_seen ? new Date(d.last_seen).toLocaleTimeString() : "—"}).
                            Disabling will silently drop every incoming face / fingerprint scan until re-enabled.
                            Employees will scan, hear the success beep, and their attendance will vanish.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="rounded-md p-3 text-xs bg-red-50 border border-red-200 text-red-900">
                        Use <b>Offline</b> or physically unplug the device instead if you just want to stop it temporarily.
                        Only choose Disable when the device is being decommissioned.
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirmDisable(false)}
                                data-testid={`hik-disable-cancel-${d.id}`}>Cancel</Button>
                        <Button className="bg-red-600 hover:bg-red-700 text-white"
                                onClick={() => { setConfirmDisable(false); enable(false); }}
                                data-testid={`hik-disable-confirm-btn-${d.id}`}>
                            Yes, disable
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                <KV label="Serial" value={d.serial_number} testid={`hik-sn-${d.id}`} />
                <KV label="MAC" value={d.mac_address || "—"} testid={`hik-mac-${d.id}`} />
                <KV label="IP address" value={d.ip_address || "not configured"} testid={`hik-ip-${d.id}`} />
                <KV label="Worksite" value={d.worksite || "—"} />
                <KV label="Last event" value={lastSeenLabel} testid={`hik-lastseen-${d.id}`} />
                <KV label="Events today" value={String(d.events_today ?? 0)} />
                {/* Iter60.p16 — dedicated user-push timestamp. `never` = amber warning. */}
                <KV
                    label="Last user push"
                    value={d.last_users_sync_at ? formatDateTime(d.last_users_sync_at) : "never"}
                    tone={d.last_users_sync_at ? "ok" : "warn"}
                    testid={`hik-users-sync-${d.id}`}
                />
                <KV
                    label="Credentials"
                    value={d.has_hikvision_password ? "stored (encrypted)" : "MISSING — set them"}
                    tone={d.has_hikvision_password ? "ok" : "warn"}
                    testid={`hik-creds-${d.id}`}
                />
            </div>

            {/* Iter60.p16 — amber hint: scans arriving but users never pushed.
                Bridges the "empty Employee column" bug back to the fix path
                on the Attendance page. */}
            {!d.last_users_sync_at && (d.events_today ?? 0) > 0 && (
                <div
                    className="rounded-md p-3 text-xs"
                    style={{ background: "#FFF7ED", border: "1px solid #FCD9A6", color: "#92400E" }}
                    data-testid={`hik-users-never-synced-${d.id}`}
                >
                    <div className="font-semibold mb-1">This device has active scans but users have never been pushed.</div>
                    <div className="leading-snug">
                        Any unmatched scans can be linked via the <b>Link</b> button on the Attendance page&apos;s Events section
                        (or run <b>Sync Users</b> below once the terminal is reachable so future scans resolve automatically).
                    </div>
                </div>
            )}

            <div className="rounded-md p-3 text-xs space-y-2" style={{ background: "#FAF7F8", border: "1px solid #F2D3DA" }}>
                <div className="font-semibold asl-navy-text">Device push endpoints — paste into the terminal&apos;s Listening Host</div>
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded">PREVIEW</span>
                    <CopyChip label="Preview URL" value={previewPush} testid={`hik-preview-url-${d.id}`} />
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-emerald-100 text-emerald-800 rounded">PRODUCTION</span>
                    <CopyChip label="Production URL" value={prodPush} testid={`hik-prod-url-${d.id}`} />
                </div>
                <div className="text-[11px] text-[color:var(--asl-muted)]">
                    Auth mode: no push token — device is authorised by matching <span className="mono">serialNo</span> in
                    the payload against the registered device row. Unknown SNs are dropped with HTTP 200.
                </div>
            </div>

            {ipLooksPrivate && (
                <div
                    className="rounded-md p-3 text-xs"
                    style={{ background: "#FFF7ED", border: "1px solid #FCD9A6", color: "#92400E" }}
                    data-testid={`hik-lan-warning-${d.id}`}
                >
                    <div className="font-semibold mb-1">Private LAN — outbound admin actions will time out</div>
                    <div className="leading-snug">
                        This device is on a private LAN{d.ip_address ? ` (${d.ip_address})` : " (no IP set)"}. The cloud
                        backend cannot reach it directly — <b>Test Connection</b>, <b>Apply Config</b>, and <b>Sync Users</b>{" "}
                        will time out. This is expected. Push mode still works: the device pushes attendance events{" "}
                        <b>TO</b> ASL over the internet without needing inbound reachability.
                    </div>
                </div>
            )}

            {isAdmin && (
                <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" disabled={busy}
                        onClick={() => setCredsOpen(true)}
                        data-testid={`hik-credentials-${d.id}`}>
                        Credentials
                    </Button>
                    <Button variant="outline" size="sm" disabled={busy || isDisabled}
                        title={isDisabled ? "Enable this device first" : undefined}
                        onClick={doTest} data-testid={`hik-test-${d.id}`}>
                        <RefreshCw className="h-3.5 w-3.5 mr-1" /> Test Connection
                    </Button>
                    <Button variant="outline" size="sm" disabled={busy || isDisabled}
                        title={isDisabled ? "Enable this device first" : undefined}
                        onClick={doApply} data-testid={`hik-apply-${d.id}`}>
                        <Send className="h-3.5 w-3.5 mr-1" /> Apply Config
                    </Button>
                    <Button variant="outline" size="sm" disabled={busy || isDisabled}
                        title={isDisabled ? "Enable this device first" : undefined}
                        onClick={doSync} data-testid={`hik-sync-${d.id}`}>
                        <Activity className="h-3.5 w-3.5 mr-1" /> Sync Users
                    </Button>
                    <Button variant="outline" size="sm" disabled={busy}
                        onClick={doSyncConfig} data-testid={`bio-syncconfig-${d.serial_number}`}
                        title="Push the fixed attendance mode (no employee picker) to this terminal">
                        <RefreshCw className="h-3.5 w-3.5 mr-1" /> Sync Config
                    </Button>
                    <Button variant="outline" size="sm" disabled={busy}
                        onClick={doProbeMode} data-testid={`bio-probe-${d.serial_number}`}
                        title="Read the device's current attendance-mode config over ISAPI (diagnostic)">
                        <Activity className="h-3.5 w-3.5 mr-1" /> Probe Mode
                    </Button>
                    {/* Iter60.p6 — Delete button, parity with LAN cards. */}
                    <Button variant="outline" size="sm" disabled={busy}
                        className="text-red-700 border-red-200 hover:bg-red-50"
                        onClick={() => setConfirmHikDelete(true)}
                        data-testid={`bio-delete-${d.serial_number}`}
                        title="Soft-delete this device and free the pairing slot">
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                    </Button>
                </div>
            )}

            {/* Iter60.p6 — Delete confirmation for Hikvision cards. */}
            <Dialog open={confirmHikDelete} onOpenChange={(o) => !o && setConfirmHikDelete(false)}>
                <DialogContent className="max-w-md" data-testid={`bio-delete-confirm-${d.serial_number}`}>
                    <DialogHeader>
                        <DialogTitle className="text-red-700 flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5" />
                            Delete device &ldquo;{d.device_name || d.serial_number}&rdquo;?
                        </DialogTitle>
                        <DialogDescription className="text-xs">
                            This will free the pairing slot on <b>{module?.name || "this module"}</b> so you can register a
                            replacement. Attendance events already tied to this device stay intact — only the device row
                            is soft-deleted and can be restored later by an admin.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="rounded-md p-3 text-xs bg-slate-50 border border-slate-200">
                        <div><span className="font-semibold">Serial:</span> <span className="mono">{d.serial_number || "—"}</span></div>
                        <div><span className="font-semibold">IP:</span> <span className="mono">{d.ip_address || "—"}</span></div>
                        <div><span className="font-semibold">Role:</span> {d.role || "unset"}</div>
                        <div><span className="font-semibold">Status:</span> {d.status || "offline"}</div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirmHikDelete(false)}
                                data-testid={`bio-delete-cancel-${d.serial_number}`}>Cancel</Button>
                        <Button className="bg-red-600 hover:bg-red-700 text-white"
                                onClick={deleteHikDevice} disabled={busy}
                                data-testid={`bio-delete-confirm-btn-${d.serial_number}`}>
                            {busy ? <><RefreshCw className="h-3 w-3 mr-1 animate-spin" /> Deleting…</> : <>Yes, delete</>}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {noVariantWorked && (
                <div
                    className="rounded-md p-3 text-xs bg-amber-50 border-2 border-amber-400 text-amber-900 space-y-2"
                    data-testid={`bio-manual-fallback-${d.serial_number}`}
                >
                    <div className="font-semibold flex items-center gap-1.5 text-sm">
                        <Activity className="h-4 w-4" />
                        Automatic mode-lock failed for this device firmware.
                    </div>
                    <div className="text-[11px] leading-relaxed text-amber-800">
                        The cloud backend cannot reach devices on your private office LAN.
                        Please disable the on-device picker manually from the device&apos;s web admin:
                    </div>
                    <ol className="text-[11px] leading-relaxed list-decimal pl-4 space-y-1">
                        <li>
                            On a computer connected to the same office network as the device, open a browser and go to:{" "}
                            <a
                                href={`https://${deviceIp}`}
                                target="_blank" rel="noopener noreferrer"
                                className="font-mono underline break-all"
                                data-testid={`bio-manual-ip-link-${d.serial_number}`}
                            >https://{deviceIp}</a>
                            {" "}(accept the &ldquo;not secure&rdquo; warning — the device uses a self-signed certificate)
                        </li>
                        <li>Log in with your device admin credentials.</li>
                        <li>In the top menu, click <span className="font-semibold">&ldquo;System and Maintenance&rdquo;</span>.</li>
                        <li>
                            In the left sidebar, click <span className="font-semibold">&ldquo;System Configuration&rdquo;</span> → then click{" "}
                            <span className="font-semibold">&ldquo;Platform Attendance&rdquo;</span> (at the bottom of the sidebar).
                        </li>
                        <li>
                            Turn <span className="font-semibold">OFF</span> the{" "}
                            <span className="font-semibold">&ldquo;Attendance Status Required&rdquo;</span> toggle
                            (it will change from green to grey).
                        </li>
                        <li>Click <span className="font-semibold">&ldquo;Save&rdquo;</span> at the bottom of the page.</li>
                        <li>
                            Test on the physical device: present your face <span className="italic">without touching the screen</span>.
                            The device should now just say <span className="font-semibold">&ldquo;Done&rdquo;</span> (or similar) — no more Check-In / Check-Out picker.
                        </li>
                    </ol>
                    <div className="text-[11px] leading-relaxed text-amber-800 italic">
                        Note: our backend already knows each device&apos;s role (check-in or check-out) and stamps
                        scans automatically. Turning off &ldquo;Attendance Status Required&rdquo; removes the on-device
                        picker without affecting attendance logging.
                    </div>
                    <div className="text-[10px] text-amber-700">
                        Verified working on DS-K1T342MFWX-E1 firmware V4.39.180.
                    </div>
                    <Button size="sm" variant="outline"
                            className="h-7 text-[11px] bg-white hover:bg-amber-100 border-amber-400 text-amber-900"
                            data-testid={`bio-copy-manual-${d.serial_number}`}
                            onClick={async () => {
                                try {
                                    if (navigator.clipboard && navigator.clipboard.writeText) {
                                        await navigator.clipboard.writeText(manualInstructionsText);
                                        toast.success("Copied to clipboard");
                                    } else {
                                        // Older-browser fallback: temp textarea + execCommand.
                                        const ta = document.createElement("textarea");
                                        ta.value = manualInstructionsText;
                                        ta.style.position = "fixed"; ta.style.opacity = "0";
                                        document.body.appendChild(ta);
                                        ta.select(); document.execCommand("copy");
                                        document.body.removeChild(ta);
                                        toast.success("Copied to clipboard");
                                    }
                                } catch (err) {
                                    toast.error("Copy failed — please copy the instructions above manually.");
                                }
                            }}>
                        Copy manual instructions
                    </Button>
                </div>
            )}

            {testResult && (
                <div
                    className="rounded-md p-3 text-[11px] mono whitespace-pre-wrap"
                    style={{ background: testResult.ok ? "#F0FDF4" : "#FEF2F2",
                             border: `1px solid ${testResult.ok ? "#BBF7D0" : "#FECACA"}` }}
                    data-testid={`hik-test-result-${d.id}`}
                >
                    {JSON.stringify(testResult, null, 2).slice(0, 800)}
                </div>
            )}

            <HikvisionCredsDialog
                open={credsOpen} onClose={() => setCredsOpen(false)}
                device={d}
                onSaved={() => { setCredsOpen(false); onRefresh?.(); }}
            />
            <ProbeModeDialog
                open={probeOpen} onClose={() => setProbeOpen(false)}
                data={probeData} device={d}
            />
        </div>
    );
}

// Iter60.p3 — Probe Mode modal. Shows the raw ISAPI response from the
// device so the admin can see what mode it thinks it's in right now.
function ProbeModeDialog({ open, onClose, data, device }) {
    if (!open) return null;
    const attendance = data?.attendance_mode || {};
    const attBody = attendance.body || {};
    const currentMode = ((attBody || {}).AttendanceMode || {}).mode;
    const deviceInfoBody = data?.device_info?.body || {};
    const info = deviceInfoBody.DeviceInfo || deviceInfoBody || {};
    const firmware = info.firmwareVersion;
    const model = info.model;
    return (
        <div
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={onClose}
            data-testid={`bio-probe-modal-${device.serial_number}`}
        >
            <div
                className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="px-4 py-3 border-b flex items-center justify-between">
                    <div>
                        <div className="text-sm font-bold asl-navy-text">Probe Mode — {device.device_name || device.serial_number}</div>
                        <div className="text-[11px] text-[color:var(--asl-muted)]">
                            {model ? `${model} · ` : ""}{firmware ? `firmware ${firmware}` : ""}
                        </div>
                    </div>
                    <button className="text-[11px] px-2 py-1 rounded border" onClick={onClose}
                            data-testid={`bio-probe-close-${device.serial_number}`}>Close</button>
                </div>
                <div className="p-4 space-y-3 overflow-auto">
                    <div className="grid grid-cols-2 gap-3 text-xs">
                        <div>
                            <div className="text-[10px] uppercase text-[color:var(--asl-muted)]">Reachable</div>
                            <div className={data?.reachable ? "text-emerald-700 font-semibold" : "text-red-700 font-semibold"}
                                 data-testid={`bio-probe-reachable-${device.serial_number}`}>
                                {data?.reachable ? "Yes" : "No"}
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase text-[color:var(--asl-muted)]">Current Attendance Mode</div>
                            <div className="font-mono asl-navy-text font-semibold"
                                 data-testid={`bio-probe-mode-${device.serial_number}`}>
                                {currentMode || "—"}
                            </div>
                        </div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase text-[color:var(--asl-muted)] mb-1">Raw /ISAPI response</div>
                        <pre className="text-[11px] bg-slate-50 border rounded p-2 overflow-auto max-h-64 whitespace-pre-wrap break-all">
{JSON.stringify(data, null, 2)}
                        </pre>
                    </div>
                    {data?.error && (
                        <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded p-2">
                            Error: {data.error}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function KV({ label, value, testid, tone }) {
    const toneCls = tone === "warn" ? "text-amber-700" : tone === "ok" ? "text-emerald-700" : "asl-navy-text";
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wide text-[color:var(--asl-muted)]">{label}</div>
            <div className={`text-xs font-medium ${toneCls} truncate`} data-testid={testid}>{value}</div>
        </div>
    );
}

function HikvisionCredsDialog({ open, onClose, device, onSaved }) {
    const [ip, setIp] = useState("");
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        if (open && device) {
            setIp(device.ip_address || "");
            setUsername(device.hikvision_username || "admin");
            setPassword("");
        }
    }, [open, device]);
    if (!device) return null;
    const save = async () => {
        setBusy(true);
        try {
            const body = { ip_address: ip || null, hikvision_username: username || null };
            if (password) body.hikvision_password = password;
            await api.post(`/devices/${device.id}/hikvision/credentials`, body);
            toast.success("Credentials saved (password encrypted at rest).");
            onSaved?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setBusy(false); }
    };
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent className="max-w-md" data-testid="hik-creds-dialog">
                <DialogHeader>
                    <DialogTitle className="asl-navy-text">Hikvision device credentials</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 text-sm">
                    <div className="space-y-1">
                        <Label htmlFor="hik-ip">IP address (on the site LAN)</Label>
                        <Input id="hik-ip" value={ip} onChange={(e) => setIp(e.target.value)}
                            placeholder="e.g. 192.168.1.64" data-testid="hik-creds-ip" />
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="hik-user">Admin username</Label>
                        <Input id="hik-user" value={username} onChange={(e) => setUsername(e.target.value)}
                            data-testid="hik-creds-user" />
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="hik-pw">Admin password</Label>
                        <div className="relative">
                            <Input id="hik-pw" type={showPw ? "text" : "password"} value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder={device.has_hikvision_password ? "leave blank to keep current" : "required"}
                                className="pr-10"
                                autoComplete="new-password"
                                data-testid="hik-creds-password" />
                            <button type="button" onClick={() => setShowPw((v) => !v)}
                                aria-label={showPw ? "Hide password" : "Show password"}
                                className="absolute inset-y-0 right-0 flex items-center px-3 text-[color:var(--asl-muted)]"
                                data-testid="hik-creds-password-toggle">
                                {showPw ? "🙈" : "👁"}
                            </button>
                        </div>
                        <div className="text-[11px] text-[color:var(--asl-muted)]">
                            Stored Fernet-encrypted at rest. Only used server-side to sign outbound Digest requests to the device.
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button className="asl-navy-bg text-white" onClick={save} disabled={busy || (!device.has_hikvision_password && !password)}
                        data-testid="hik-creds-save">
                        {busy ? "Saving…" : "Save"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function AddDeviceDialog({ open, onClose, modules, devices, prefill, onSaved }) {
    const [form, setForm] = useState({
        device_name: "", serial_number: "", module_id: "", role: "check_in", worksite: "",
        connection_type: "adms_push", device_model: "ZKTeco PRO 200",
        ip_address: "", tcp_port: 4370, time_zone: "Africa/Mogadishu",
        mac_address: "", firmware_version: "",
        hikvision_username: "admin", hikvision_password: "",
    });
    const [busy, setBusy] = useState(false);

    // Compute which roles are taken in the currently-selected module
    // so the role select can disable duplicates + full modules.
    // Iter60.p6 — disabled devices don't reserve a slot (mirrors the
    // backend `validate_device_slot` fix and the PairingSummary filter).
    // A disabled device is effectively decommissioned; the dropdown
    // must still let admins pick that role for the replacement.
    const takenRoles = useMemo(() => {
        if (!form.module_id || !devices) return new Set();
        return new Set(
            (devices || [])
                .filter((d) => !d.deleted_at
                            && d.status !== "disabled"
                            && d.module_id === form.module_id)
                .map((d) => d.role || "check_in")
        );
    }, [form.module_id, devices]);
    const moduleFull = takenRoles.size >= 2;

    useEffect(() => {
        if (!open) return;
        // Apply prefill from a "Quick-add" click on an empty slot.
        if (prefill) {
            setForm((f) => ({ ...f, module_id: prefill.module_id, role: prefill.role }));
            return;
        }
        if (modules.length && !form.module_id) {
            setForm((f) => ({ ...f, module_id: modules[0].id }));
        }
    }, [open, prefill, modules, form.module_id]);

    // When the selected module changes and the current role is already
    // taken, auto-pick the other role (if any is free).
    useEffect(() => {
        if (!open || !form.module_id) return;
        if (takenRoles.has(form.role)) {
            const fallback = DEVICE_ROLES.find((r) => !takenRoles.has(r.key));
            if (fallback) setForm((f) => ({ ...f, role: fallback.key }));
        }
    }, [open, form.module_id, form.role, takenRoles]);

    const save = async () => {
        if (!form.device_name || !form.serial_number || !form.module_id) {
            toast.error("Name, serial and module are required");
            return;
        }
        if (moduleFull) {
            toast.error("This module already has 2 devices");
            return;
        }
        if (takenRoles.has(form.role)) {
            toast.error(`This module already has a ${form.role.replace("_", "-")} device`);
            return;
        }
        if (form.connection_type === "hikvision_isapi" && !form.hikvision_password) {
            toast.error("Admin password is required for a Hikvision device");
            return;
        }
        setBusy(true);
        try {
            const payload = { ...form };
            if (!["pull_sdk", "hikvision_isapi"].includes(form.connection_type)) delete payload.ip_address;
            if (form.connection_type !== "hikvision_isapi") {
                delete payload.hikvision_username;
                delete payload.hikvision_password;
                delete payload.mac_address;
                delete payload.firmware_version;
            }
            const createResp = await api.post("/devices", payload);
            toast.success("Device created");
            // Iter60.p2 — for Hikvision terminals, immediately try to lock
            // the fixed attendance mode so employees don't get the picker.
            if (form.connection_type === "hikvision_isapi" && createResp?.data?.id) {
                try {
                    const { data: syncRes } = await api.post(`/biometric-devices/${createResp.data.id}/sync-config`);
                    if (syncRes?.ok) {
                        toast.success(`Terminal locked to ${form.role === "check_out" ? "Check-Out" : "Check-In"} mode`);
                    } else if (syncRes?.error === "unreachable") {
                        toast.warning("Device registered but couldn't reach it to lock mode; click Sync Config once network is available.");
                    } else {
                        toast.warning(`Mode sync issue: ${syncRes?.error || "unknown"} — retry with Sync Config.`);
                    }
                } catch (syncErr) {
                    toast.warning("Device registered; mode sync failed — retry with Sync Config once reachable.");
                }
            }
            onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-lg" data-testid="bio-add-dialog">
                <DialogHeader><DialogTitle>Add Biometric Device</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <Row label="Name"><Input value={form.device_name} onChange={(e) => setForm({ ...form, device_name: e.target.value })} data-testid="bio-add-name" /></Row>
                    <Row label="Serial Number"><Input value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} placeholder="ZKPRO200-AP-0001" data-testid="bio-add-sn" /></Row>
                    <Row label="Module">
                        <select className="w-full border rounded-md p-2 text-sm" value={form.module_id}
                                onChange={(e) => setForm({ ...form, module_id: e.target.value })} data-testid="bio-add-module">
                            {modules.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                        </select>
                    </Row>
                    <Row label="Role">
                        <select className="w-full border rounded-md p-2 text-sm" value={form.role}
                                onChange={(e) => setForm({ ...form, role: e.target.value })}
                                data-testid="bio-add-role" disabled={moduleFull}>
                            {DEVICE_ROLES.map((r) => {
                                const taken = takenRoles.has(r.key);
                                return (
                                    <option key={r.key} value={r.key} disabled={taken}
                                        data-testid={`bio-add-role-opt-${r.key}`}>
                                        {r.label}{taken ? " — already assigned" : ""}
                                    </option>
                                );
                            })}
                        </select>
                        {moduleFull && (
                            <div className="text-[11px] mt-1" style={{ color: "var(--status-error)" }}
                                data-testid="bio-add-module-full">
                                This module already has 2 devices — delete one first.
                            </div>
                        )}
                    </Row>
                    <Row label="Worksite"><Input value={form.worksite} onChange={(e) => setForm({ ...form, worksite: e.target.value })} data-testid="bio-add-worksite" /></Row>
                    <Row label="Connection Type">
                        <select className="w-full border rounded-md p-2 text-sm" value={form.connection_type}
                                onChange={(e) => {
                                    const ct = e.target.value;
                                    setForm({
                                        ...form,
                                        connection_type: ct,
                                        device_model: ct === "hikvision_isapi" ? "DS-K1T342MFWX-E1" : (ct === "adms_push" ? "ZKTeco PRO 200" : form.device_model),
                                    });
                                }} data-testid="bio-add-conn">
                            <option value="adms_push">ADMS Push (cloud)</option>
                            <option value="pull_sdk">Pull SDK (LAN)</option>
                            <option value="hikvision_isapi">Hikvision ISAPI (push)</option>
                            <option value="mock">Mock / Generator</option>
                        </select>
                    </Row>
                    <Row label="Device Model"><Input value={form.device_model} onChange={(e) => setForm({ ...form, device_model: e.target.value })} data-testid="bio-add-model" /></Row>
                    {form.connection_type === "pull_sdk" && (
                        <>
                            <Row label="IP Address"><Input value={form.ip_address} onChange={(e) => setForm({ ...form, ip_address: e.target.value })} placeholder="10.0.1.42" data-testid="bio-add-ip" /></Row>
                            <Row label="TCP Port"><Input type="number" value={form.tcp_port} onChange={(e) => setForm({ ...form, tcp_port: parseInt(e.target.value, 10) || 4370 })} data-testid="bio-add-port" /></Row>
                        </>
                    )}
                    {form.connection_type === "hikvision_isapi" && (
                        <>
                            <Row label="MAC Address">
                                <Input value={form.mac_address} onChange={(e) => setForm({ ...form, mac_address: e.target.value })}
                                    placeholder="88:de:39:4d:58:45" data-testid="bio-add-mac" />
                            </Row>
                            <Row label="Firmware">
                                <Input value={form.firmware_version} onChange={(e) => setForm({ ...form, firmware_version: e.target.value })}
                                    placeholder="V4.39.180" data-testid="bio-add-fw" />
                            </Row>
                            <Row label="IP Address (LAN)">
                                <Input value={form.ip_address} onChange={(e) => setForm({ ...form, ip_address: e.target.value })}
                                    placeholder="e.g. 192.168.1.64 (optional — can be set later)" data-testid="bio-add-hik-ip" />
                            </Row>
                            <Row label="Admin Username">
                                <Input value={form.hikvision_username} onChange={(e) => setForm({ ...form, hikvision_username: e.target.value })}
                                    data-testid="bio-add-hik-user" />
                            </Row>
                            <Row label="Admin Password">
                                <Input type="password" value={form.hikvision_password}
                                    onChange={(e) => setForm({ ...form, hikvision_password: e.target.value })}
                                    autoComplete="new-password"
                                    placeholder="required — stored encrypted"
                                    data-testid="bio-add-hik-pw" />
                            </Row>
                            <div className="text-[11px] text-[color:var(--asl-muted)] mt-1">
                                Push receiver on our side: <span className="mono">/api/hikvision/push</span>.
                                Configure that URL on the terminal&apos;s Listening Host settings.
                            </div>
                        </>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} data-testid="bio-add-cancel">Cancel</Button>
                    <Button onClick={save} className="asl-navy-bg text-white" disabled={busy} data-testid="bio-add-save">Create</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function EditDeviceDialog({ device, modules, onClose, onSaved }) {
    const [form, setForm] = useState({});
    const [busy, setBusy] = useState(false);
    useEffect(() => { setForm(device || {}); }, [device]);
    if (!device) return null;

    const save = async () => {
        setBusy(true);
        try {
            const patch = {};
            ["device_name", "serial_number", "module_id", "worksite", "ip_address", "tcp_port", "time_zone"].forEach((k) => {
                if (form[k] !== device[k]) patch[k] = form[k];
            });
            if (Object.keys(patch).length === 0) { toast.info("No changes"); onClose(); return; }
            await api.patch(`/devices/${device.id}`, patch);
            toast.success("Saved");
            onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-lg" data-testid="bio-edit-dialog">
                <DialogHeader><DialogTitle>Edit {device.device_name}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <Row label="Name"><Input value={form.device_name || ""} onChange={(e) => setForm({ ...form, device_name: e.target.value })} /></Row>
                    <Row label="Serial Number"><Input value={form.serial_number || ""} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} /></Row>
                    <Row label="Module">
                        <select className="w-full border rounded-md p-2 text-sm" value={form.module_id || ""}
                                onChange={(e) => setForm({ ...form, module_id: e.target.value })}>
                            {modules.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                        </select>
                    </Row>
                    <Row label="Worksite"><Input value={form.worksite || ""} onChange={(e) => setForm({ ...form, worksite: e.target.value })} /></Row>
                    {device.connection_type === "pull_sdk" && (
                        <>
                            <Row label="IP Address"><Input value={form.ip_address || ""} onChange={(e) => setForm({ ...form, ip_address: e.target.value })} /></Row>
                            <Row label="TCP Port"><Input type="number" value={form.tcp_port || 4370} onChange={(e) => setForm({ ...form, tcp_port: parseInt(e.target.value, 10) || 4370 })} /></Row>
                        </>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={save} className="asl-navy-bg text-white" disabled={busy}>Save device</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function CommandDialog({ device, onClose }) {
    const [command, setCommand] = useState("set_time");
    const [payloadStr, setPayloadStr] = useState("{}");
    const [busy, setBusy] = useState(false);
    if (!device) return null;
    const queue = async () => {
        let payload = {};
        try { payload = payloadStr ? JSON.parse(payloadStr) : {}; }
        catch { toast.error("Payload must be valid JSON"); return; }
        setBusy(true);
        try {
            await api.post(`/devices/${device.id}/queue-command`, { command, payload });
            toast.success(`Queued ${command}`);
            onClose();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };
    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md" data-testid="bio-cmd-dialog">
                <DialogHeader><DialogTitle>Queue Command — {device.device_name}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <Row label="Command">
                        <select className="w-full border rounded-md p-2 text-sm" value={command} onChange={(e) => setCommand(e.target.value)}
                                data-testid="bio-cmd-select">
                            <option value="update_user">update_user</option>
                            <option value="delete_user">delete_user</option>
                            <option value="push_fingerprint">push_fingerprint</option>
                            <option value="push_face">push_face</option>
                            <option value="clear_data">clear_data</option>
                            <option value="reboot">reboot</option>
                            <option value="set_time">set_time</option>
                        </select>
                    </Row>
                    <Row label="Payload (JSON)">
                        <textarea className="w-full h-24 border rounded-md p-2 text-xs mono" value={payloadStr}
                                  onChange={(e) => setPayloadStr(e.target.value)} data-testid="bio-cmd-payload" />
                    </Row>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={queue} className="asl-navy-bg text-white" disabled={busy} data-testid="bio-cmd-queue">Queue</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function QueueDialog({ device, onClose }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        if (!device) return;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/devices/${device.id}/commands`);
                setItems(data);
            } catch (e) { toast.error(formatApiError(e)); }
            finally { setLoading(false); }
        })();
    }, [device]);
    if (!device) return null;
    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-2xl" data-testid="bio-queue-dialog">
                <DialogHeader><DialogTitle>Command queue — {device.device_name}</DialogTitle></DialogHeader>
                {loading ? <div className="text-sm">Loading…</div> : (
                    items.length === 0 ? <div className="text-sm text-[color:var(--asl-muted)] italic">No commands.</div> : (
                        <div className="space-y-1 max-h-96 overflow-y-auto text-xs">
                            {items.map((c) => (
                                <div key={c.id} className="border rounded-md p-2 flex items-center justify-between gap-2">
                                    <div>
                                        <div className="font-semibold asl-navy-text">{c.command}</div>
                                        <div className="text-[10px] text-[color:var(--asl-muted)] mono">created {c.created_at}</div>
                                    </div>
                                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                                        c.status === "done" ? "bg-emerald-50 text-emerald-700"
                                        : c.status === "failed" ? "bg-red-50 text-red-700"
                                        : c.status === "sent" ? "bg-blue-50 text-blue-700"
                                        : "bg-gray-100 text-gray-600"
                                    }`}>{c.status}</span>
                                </div>
                            ))}
                        </div>
                    )
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Row({ label, children }) {
    return (
        <div>
            <Label className="text-xs">{label}</Label>
            <div className="mt-1">{children}</div>
        </div>
    );
}
